import React from "react"
import Select, {StylesConfig} from "react-select"
import {UsNexusTypeMapping} from "../../resources/application"
import {Editable, EditableElementProps} from "../HorizontalField/EditableHorizontalField"

interface UsNexusOption {
	readonly value: string
	readonly label: string
}

const usNexusOptions: UsNexusOption[] = Array.from(UsNexusTypeMapping.entries()).map(([value, label]) => ({
	value,
	label,
}))

const selectStyles: StylesConfig<UsNexusOption, true> = {
	control: (base, state) => ({
		...base,
		borderColor: state.isFocused ? "#000000" : base.borderColor,
		"&:hover": {
			borderColor: state.isFocused ? "#000000" : base.borderColor,
		},
		minHeight: 38,
	}),
	container: (base) => ({
		...base,
		width: "100%",
	}),
}

function EditableUsNexusInput(props: EditableElementProps<string[]>) {
	const hasUsNexus = props.value.length === 0 || !props.value.includes("NotAvailable")
	const selectedTypes = props.value.filter((t) => t !== "NotAvailable")

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked
		if (!checked) {
			props.setValue(["NotAvailable"])
		} else {
			props.setValue([])
		}
	}

	const handleSelectChange = (newValue: readonly UsNexusOption[]) => {
		if (newValue.length === 0) {
			props.setValue([])
		} else {
			props.setValue(newValue.map((v) => v.value))
		}
	}

	const displayValue = hasUsNexus
		? props.value.map((t) => UsNexusTypeMapping.get(t) || t).join(", ") || "Has US Nexus"
		: "Not Available"

	const selectedOptions = selectedTypes
		.map((t) => usNexusOptions.find((o) => o.value === t))
		.filter((o): o is UsNexusOption => o !== undefined)

	if (props.isEditing) {
		return (
			<div>
				<div className="field">
					<label className="checkbox">
						<input type="checkbox" checked={hasUsNexus} onChange={handleCheckboxChange} className="mr-2" />
						Has US Nexus
					</label>
				</div>
				{hasUsNexus && (
					<div className="field mt-2">
						<Select<UsNexusOption, true>
							isMulti
							value={selectedOptions}
							options={usNexusOptions}
							onChange={handleSelectChange}
							styles={selectStyles}
							placeholder="Select US Nexus types..."
							closeMenuOnSelect={false}
							hideSelectedOptions={false}
							menuPlacement="auto"
						/>
					</div>
				)}
			</div>
		)
	}

	return <input type="text" readOnly value={displayValue} className="input is-static" />
}

export interface EditableUsNexusProps {
	usNexus?: string[]
	setUsNexus: (value: string[]) => void
	isEditing: boolean
	isDisabled: boolean
	isAllowed: boolean
	isUpdating: boolean
	onStartEdit: () => void
	onCancel: () => void
	update: (value: string[]) => void
	className?: string
}

export function EditableUsNexusField(props: EditableUsNexusProps) {
	if (props.usNexus === undefined) {
		return null
	}

	return (
		<Editable
			label="US Nexus"
			isEditing={props.isEditing}
			isDisabled={props.isDisabled}
			isAllowed={props.isAllowed}
			onStartEdit={props.onStartEdit}
			onCancel={props.onCancel}
			setValue={props.setUsNexus}
			value={props.usNexus}
			initial={props.usNexus}
			update={props.update}
			className={props.className}
		>
			{EditableUsNexusInput}
		</Editable>
	)
}
